import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { EntityRef } from '../../components/EntityRef'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { CollapsibleSection } from '../../components/CollapsibleSection'
import { RelatedList } from '../../design-system/list-view/RelatedList'
import { TimelinePanel } from '../../components/timeline/TimelinePanel'
import { getLegalEntity } from '../../api/legalEntities'
import { listBranches } from '../../api/branches'
import { listCases } from '../../api/cases'
import { ApiError } from '../../api/problem'
import { label } from '../../lib/labels'
import { rsqlEquals } from '../../lib/rsql'
import type { LegalEntityView } from '../../types/account'
import { buildBranchColumns, BRANCH_COLUMN_ORDER } from '../branches/columns'
import { buildCaseColumns, CASE_COLUMN_ORDER } from '../cases/caseColumns'

/**
 * The first-ever LegalEntity detail page (net-new, FR-M5.2/M5.3, docs/design/
 * IB-49-crm-detail-timeline-design.md §5.4) — reached in ≤2 clicks from `LegalEntitiesListStub`'s
 * row-click or from the parent Account's Legal Entities `RelatedList` (both wired to
 * `/clients/legal-entities/:id`). Header → `CollapsibleSection` "Details" → `RelatedList` Branches +
 * `RelatedList` Cases (both real M23 `ListView`s) → `TimelinePanel` (incl. its Branches' and Cases'
 * events, resolved server-side, ADR-0046).
 */
export function LegalEntityDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [entity, setEntity] = useState<LegalEntityView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const branchColumns = useMemo(() => buildBranchColumns(), [])
  const caseColumns = useMemo(() => buildCaseColumns(), [])

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    setError(null)
    getLegalEntity(id)
      .then(({ entity: e }) => {
        if (!cancelled) setEntity(e)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not load this legal entity.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
        <Link to="/clients/legal-entities" style={{ color: 'var(--iw-text-muted)' }}>
          Legal entities
        </Link>{' '}
        › <EntityRef kind="legalEntity" id={id} name={entity?.name} />
      </div>

      {error ? (
        <InlineAlert severity="error" action={<button type="button" className="iw-btn iw-btn--sm" onClick={() => navigate(0)}>Try again</button>}>
          {error}
        </InlineAlert>
      ) : loading ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading legal entity…</p>
      ) : entity ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--iw-accent)' }}>
              <Icon name="landmark" size={22} />
            </span>
            <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>{entity.name}</h1>
          </div>

          <CollapsibleSection title="Details">
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 'var(--iw-space-4)',
                margin: 0,
              }}
            >
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Account</dt>
                <dd style={{ margin: 0 }}>
                  <EntityRef kind="account" id={entity.accountId} name={entity.accountName} href={`/clients/accounts/${entity.accountId}`} />
                </dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Jurisdiction</dt>
                <dd style={{ margin: 0 }}>{label('jurisdiction', entity.jurisdiction)}</dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Tax IDs</dt>
                <dd style={{ margin: 0 }}>{entity.taxIdentifiers.length}</dd>
              </div>
            </dl>
          </CollapsibleSection>

          <RelatedList
            storageKeyPrefix={`iw.legal-entity-detail.${entity.id}.branches`}
            entityIcon="map-pin"
            title="Branches"
            columns={branchColumns}
            defaultColumnOrder={BRANCH_COLUMN_ORDER}
            defaultSort={[{ columnId: 'name', direction: 'asc' }]}
            searchFields={['name', 'country', 'region']}
            searchPlaceholder="Search branches…"
            parentFilter={rsqlEquals('legalEntityId', entity.id)}
            fetchPage={({ filter, sort, cursor, limit }) => listBranches({ filter, sort, cursor, limit })}
            getRowId={(row) => row.id}
            emptyTitle="No branches yet"
          />

          <RelatedList
            storageKeyPrefix={`iw.legal-entity-detail.${entity.id}.cases`}
            entityIcon="briefcase"
            title="Cases"
            columns={caseColumns}
            defaultColumnOrder={CASE_COLUMN_ORDER}
            defaultSort={[{ columnId: 'overallStatus', direction: 'asc' }]}
            parentFilter={rsqlEquals('legalEntityId', entity.id)}
            fetchPage={({ filter, sort, cursor, limit }) => listCases({ filter, sort, cursor, limit })}
            getRowId={(row) => row.id}
            onRowActivate={(row) => navigate(`/cases/${row.id}`)}
            emptyTitle="No cases yet"
          />

          <section>
            <h2 style={{ fontSize: 'var(--iw-size-md)', margin: '0 0 var(--iw-space-3)' }}>Timeline</h2>
            <TimelinePanel target={{ type: 'LegalEntity', id: entity.id }} />
          </section>
        </>
      ) : null}
    </div>
  )
}
